<template lang="pug">
.report-page
  .title-section Métricas

  form(@submit.prevent="")
    .input-container.arrow
      select.select(v-model="month" :disabled="loading")
        option(v-for="item in months" :value="item.value") {{ item.label }}
      label.label.show Mes

    .input-container.arrow
      select.select(v-model="year" :disabled="loading")
        option(v-for="item in years" :value="item") {{ item }}
      label.label.show Año

  .metrics-day-tags
    button.metrics-day-tag(
      v-for="item in salesTodayByBrand"
      :key="item.brand"
      type="button"
      :class="{ 'is-active': activeBrand === item.brand }"
      :style="brandTagStyle(item.brand)"
      @click="activeBrand = item.brand"
    ) {{ capitalize(item.brand) }}

  .container-report
    h3 Métricas del mes
    .container-kpis
      .kpi-card(v-for="kpi in monthlyKpis" :key="kpi.label")
        .kpi-value
          span {{ kpi.value }}
          span.kpi-percent(v-if="kpi.percent")  {{ kpi.percent }}
        .kpi-label {{ kpi.label }}

    h3 Hoy
    .container-kpis
      .kpi-card(v-for="kpi in todayKpis" :key="kpi.label")
        .kpi-value
          span {{ kpi.value }}
        .kpi-label {{ kpi.label }}
</template>

<script>
import gql from '@my/graphql'
import gqlReport from '@/graphql/report'
import mFilters from '@/mixins/filters'

function pad (value) {
  return String(value).padStart(2, '0')
}

function formatInputDate (date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export default {
  name: 'Metrics',

  mixins: [mFilters],

  data () {
    const now = new Date()

    return {
      month: now.getMonth(),
      year: now.getFullYear(),
      activeBrand: 'sahet',
      loading: false,
      pendingLoad: false,
      metrics: {
        newCustomers: 0,
        secondPurchaseCustomers: 0,
        totalCustomers: 0,
        oldCustomers: 0,
        totalSales: 0,
        totalOrders: 0
      },
      salesTodayByBrand: []
    }
  },

  computed: {
    months () {
      return [
        { value: 0, label: 'Enero' },
        { value: 1, label: 'Febrero' },
        { value: 2, label: 'Marzo' },
        { value: 3, label: 'Abril' },
        { value: 4, label: 'Mayo' },
        { value: 5, label: 'Junio' },
        { value: 6, label: 'Julio' },
        { value: 7, label: 'Agosto' },
        { value: 8, label: 'Septiembre' },
        { value: 9, label: 'Octubre' },
        { value: 10, label: 'Noviembre' },
        { value: 11, label: 'Diciembre' }
      ]
    },
    years () {
      const currentYear = new Date().getFullYear()
      const result = []
      for (let y = currentYear; y >= 2019; y--) result.push(y)
      return result
    },
    brands () {
      const brands = this.$store.state.variables.brands || []
      return [...brands].sort((a, b) => {
        if (a === 'sahet') return -1
        if (b === 'sahet') return 1
        return 0
      })
    },
    range () {
      const lastDay = new Date(this.year, this.month + 1, 0).getDate()
      const month = pad(this.month + 1)

      return {
        from: `${this.year}-${month}-01`,
        to: `${this.year}-${month}-${pad(lastDay)}`
      }
    },
    today () {
      return formatInputDate(new Date())
    },
    visibleSales () {
      return this.salesTodayByBrand.filter(item => item.brand === this.activeBrand)
    },
    totalOrdersToday () {
      return this.visibleSales.reduce((sum, item) => sum + item.orders, 0)
    },
    totalValueToday () {
      return this.visibleSales.reduce((sum, item) => sum + item.value, 0)
    },
    averageOrderValueToday () {
      return this.totalOrdersToday > 0 ? Math.round(this.totalValueToday / this.totalOrdersToday) : 0
    },
    daysInSelectedPeriod () {
      const now = new Date()
      const isCurrentMonth = this.year === now.getFullYear() && this.month === now.getMonth()
      return isCurrentMonth ? now.getDate() : new Date(this.year, this.month + 1, 0).getDate()
    },
    averageDailySales () {
      return this.daysInSelectedPeriod > 0 ? Math.round(this.metrics.totalSales / this.daysInSelectedPeriod) : 0
    },
    averageOrderValueMonth () {
      return this.metrics.totalOrders > 0 ? Math.round(this.metrics.totalSales / this.metrics.totalOrders) : 0
    },
    monthlyKpis () {
      const newCustomers = this.metrics.newCustomers
      const secondPurchaseCustomers = this.metrics.secondPurchaseCustomers
      const totalCustomers = this.metrics.totalCustomers

      return [
        { label: 'Clientes totales', value: totalCustomers },
        { label: 'Ventas del mes', value: this.money(this.metrics.totalSales) },
        { label: 'Órdenes del mes', value: this.metrics.totalOrders },
        { label: 'Promedio de venta diaria', value: this.money(this.averageDailySales) },
        { label: 'Promedio de la orden', value: this.money(this.averageOrderValueMonth) },
        { label: 'Clientes nuevos', value: newCustomers, percent: this.percentLabel(newCustomers, totalCustomers) },
        { label: 'Clientes antiguos', value: this.metrics.oldCustomers, percent: this.percentLabel(this.metrics.oldCustomers, totalCustomers) },
        { label: 'Clientes con segunda compra', value: secondPurchaseCustomers, percent: this.percentLabel(secondPurchaseCustomers, totalCustomers) }
      ]
    },
    todayKpis () {
      return [
        { label: `Ventas de hoy · ${this.totalOrdersToday} órdenes`, value: this.money(this.totalValueToday) },
        { label: 'Promedio de venta hoy', value: this.money(this.averageOrderValueToday) }
      ]
    }
  },

  watch: {
    brands () { this.loadData() },
    month () { this.loadData() },
    year () { this.loadData() },
    activeBrand () { this.loadData() }
  },

  mounted () {
    this.loadData()
  },

  methods: {
    brandTagStyle (brand) {
      if (this.activeBrand !== brand) return {}

      return {
        background: `var(--background-tag-${brand}, #363636)`,
        color: '#fff'
      }
    },
    percentLabel (part, total) {
      if (!total) return '0%'
      return `${Math.round((part / total) * 100)}%`
    },
    async loadData () {
      if (this.loading) {
        this.pendingLoad = true
        return
      }

      this.loading = true

      const metricsPromise = gql.query(gqlReport.GET_CUSTOMER_METRICS, {
        initialDate: this.range.from,
        finalDate: this.range.to,
        cutoffDate: this.today,
        brand: this.activeBrand
      })

      const salesPromises = this.brands.map(brand => gql.query(gqlReport.GET_SALES, {
        initialDate: this.today,
        finalDate: this.today,
        brand
      }))

      const [metricsResponse, ...salesResponses] = await Promise.all([metricsPromise, ...salesPromises])

      if (metricsResponse && metricsResponse.data) {
        const metrics = metricsResponse.data.getCustomerMetrics
        this.metrics = {
          newCustomers: metrics.newCustomers,
          secondPurchaseCustomers: metrics.secondPurchaseCustomers,
          totalCustomers: metrics.totalCustomers,
          oldCustomers: metrics.oldCustomers,
          totalSales: metrics.totalSales,
          totalOrders: metrics.totalOrders
        }
      }

      this.salesTodayByBrand = this.brands.map((brand, index) => {
        const sales = (salesResponses[index] && salesResponses[index].data && salesResponses[index].data.getReportSales) || []
        const orders = sales.filter(order => {
          const methodPay = (order.methodPay || '').trim().toLowerCase()
          return methodPay !== 'bono' && methodPay !== 'bonus'
        })
        return {
          brand,
          orders: orders.length,
          value: orders.reduce((sum, order) => sum + (order.total || 0), 0)
        }
      })

      this.loading = false

      if (this.pendingLoad) {
        this.pendingLoad = false
        this.loadData()
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
.metrics-day-tags
  display flex
  flex-wrap wrap
  gap .75rem
  margin 1em 0 1.5em

.metrics-day-tag
  flex 0 0 auto
  min-height 2rem
  padding .35rem .85rem
  box-sizing border-box
  display flex
  align-items center
  justify-content center
  border none
  border-radius .35rem
  font-size .9rem
  font-weight 400
  text-transform uppercase
  opacity .4
  cursor pointer
  user-select none
  background var(--color-light, rgba(0, 0, 0, .06))
  color var(--color-font-principal, #363636)
  transition background-color .15s ease, color .15s ease, opacity .15s ease

  &.is-active
    opacity 1
    box-shadow 0 2px 8px rgba(0, 0, 0, .12)

  &:active
    transform scale(.97)

.kpi-percent
  margin-left .35em
  font-size 1.1rem
  font-weight 400
  color rgba(0, 0, 0, .45)
</style>



// WEBPACK FOOTER //
// src/components/pages/metrics/Metrics.vue